import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Key, AlertCircle, Zap, Loader2 } from 'lucide-react';

interface LoginProps {
  onLogin: () => void;
}

export function Login({ onLogin }: LoginProps) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/admin/stats`, {
        headers: {
          'X-API-Key': apiKey,
        },
      });

      if (response.ok) {
        localStorage.setItem('admin_api_key', apiKey);
        onLogin();
      } else if (response.status === 401) {
        setError('Invalid API key');
      } else {
        setError('Failed to verify API key');
      }
    } catch (err) {
      setError('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-background via-background to-muted/10 pointer-events-none" />
      
      <div className="w-full max-w-sm relative space-y-8">
        {/* Logo */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-foreground">
            <Zap className="h-8 w-8 text-background" />
          </div>
          <h1 className="text-2xl font-semibold">ClawdBot Factory</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter your API key to continue
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Key className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="cbf_xxxxx..."
              required
              className="pl-12 h-14 rounded-2xl text-base font-mono"
              autoFocus
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-3 text-sm text-status-error-foreground bg-status-error/20 border border-status-error rounded-xl px-4 py-3">
              <AlertCircle className="h-5 w-5 shrink-0" />
              {error}
            </div>
          )}
          
          <Button type="submit" size="xl" className="w-full rounded-2xl" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Login'
            )}
          </Button>
        </form>
        
        <p className="text-center text-xs text-muted-foreground">
          Contact support if you need access
        </p>
      </div>
    </div>
  );
}
